/* eslint-disable @typescript-eslint/no-explicit-any */
import {useEffect, useState} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {Image} from '@jobseeker/lotus/components/Image';
import {Typography, Box, Link, Card, Grid, Skeleton} from '@mui/material';
import kebabCase from 'lodash/kebabCase';

import useLoadAds from '@jobseeker/common-core/hooks/useLoadAds';
import {getOldLink} from '@jobseeker/lotus/utils';

import type {AdElement} from '../../types';
import type {
  AppState,
  AdsElementType,
  ProfileTags,
} from '@jobseeker/common-core/types';
import type {} from '@jobseeker/lotus/mui.types';
import {
  handleTracking,
  TrackEventUBA,
  getUbaPageName,
} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';

const PLATFORM = process.env.NEXT_PUBLIC_APP_NAME || '';
const isAppHirist = PLATFORM === 'hirist';

const LearnGrow = ({
  slot,
  label,
  adsEventInfo,
  titleOut = false,
}: AdElement) => {
  const [learnGrow, setLearnGrow] = useState(Array<AdsElementType>);

  const [adsLoaded, setAdsLoaded] = useState(false);

  const learnGrowInfo = useSelector(
    (state: AppState) => state.ads.learnGrow,
    shallowEqual
  );

  const categoryId = useSelector(
    (state: AppState) => state.app.context.navigation.actualCategory
  );
  const tagId = useSelector(
    (state: AppState) => state.app.context.navigation.tag,
    shallowEqual
  );

  const [adOptions, setAdOptions] = useState({
    page: 0,
    size: 20,
    exp: '',
    user_location: '0',
    categoryIds: categoryId ? categoryId?.toString() : 0,
    tags: '',
    shuffle: 1,
    orderby: 'modified',
    logged: 1,
  });

  const {userId, isLoggedIn} = useSelector(
    (state: AppState) => state.auth,
    shallowEqual
  );

  const {
    profile: {experienceYear, locations, tags, id},
  } = useSelector((state: AppState) => state.user, shallowEqual);

  useEffect(() => {
    if (userId !== '-1' && isLoggedIn && id) {
      setAdOptions({
        page: 0,
        size: 20,
        exp: experienceYear ? experienceYear.toString() : '',
        user_location: locations?.current?.id
          ? locations?.current?.id?.toString()
          : '0',
        categoryIds: categoryId ? categoryId?.toString() : '0',
        tags:
          tags && tags?.length
            ? tags?.map((tag: ProfileTags) => tag?.tagId).join(',')
            : '',
        shuffle: 1,
        orderby: 'modified',
        logged: 2,
      });
    }
  }, [isLoggedIn, userId, id]);

  useLoadAds(
    slot,
    setLearnGrow,
    adOptions,
    true,
    !learnGrowInfo[0]?.title,
    setAdsLoaded
  );

  const loadedLearnGrow = learnGrowInfo[0]?.title ? learnGrowInfo : learnGrow;

  const page = useSelector((state: AppState) => state.app.page, shallowEqual);

  const callEvent = (type: 'view' | 'click') => {
    if (adsEventInfo && adsEventInfo[type])
      handleTracking(adsEventInfo[type], {
        event_category: adsEventInfo['eventCategory'],
        Title: loadedLearnGrow[0]?.title,
        Origin: page,
        Source: 'AdsSection',
        Type: type,
        UserId: sessionStorage.getItem('userId') || 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Device_Type: sessionStorage.getItem('deviceType') || 'web',
        Ref: 'logo',
        Refinv: 'featured',
        Status: sessionStorage.getItem('status') || 'LoggedOut',
      });
  };

  const router = useRouter();

  const trackEvent = (adItem: {
    courseRefCode: number | string;
    id?: string;
    title?: string;
    courseId?: string;
  }) => {
    let eventName = 'courseClick';
    let eventData: Record<string, any> = {
      categoryID: page == 'category' ? categoryId : undefined,
      tagID: page == 'tag' ? Number(tagId) : undefined,
      courseRefCode: router.query.ref || '',
      courseID: adItem.courseId || '',
      refInv: 'lg',
      refOrg: 'sd',
      page: getUbaPageName(),
      sessionUsername: sessionStorage.getItem('userId') || '',
      ref: new URL(window.location.href).searchParams.get('ref') || '',
    };

    if (label?.toLowerCase()?.includes('institute')) {
      eventName = 'featuredInstituteClick';
      eventData.featuredInstituteId = adItem.id || '';
      eventData.featuredInstituteName = adItem.title || '';
    }

    TrackEventUBA(eventName, 'click', eventData);
  };

  useEffect(() => {
    if (loadedLearnGrow?.length > 0) {
      callEvent('view');
    }
  }, [loadedLearnGrow]);

  return (
    <>
      {adsLoaded ? (
        loadedLearnGrow?.length > 0 && (
          <>
            {titleOut && (
              <Typography
                component={'span'}
                fontSize={16}
                fontWeight={500}
                sx={{mb: 1.5}}
              >
                {label}
              </Typography>
            )}
            <Card
              sx={{
                mt: titleOut ? 1.5 : 2,
                p: 2,
                borderRadius: titleOut ? 2 : 4,
                mb: titleOut ? 3.75 : 0,
                ...(isAppHirist && {pt: 0}),
              }}
              id={`job_ads_banner_${label?.toLowerCase()?.replace(/ /g, '-')}`}
              data-testid={`job_ads_banner_${label
                ?.toLowerCase()
                ?.replace(/ /g, '-')}`}
            >
              {!titleOut && (
                <Typography component={'span'} fontSize={14} fontWeight={500}>
                  {label}
                </Typography>
              )}

              {loadedLearnGrow?.map((adObj: any, index: number) => (
                <Box key={`ads-learnngrow-img-${index}`} sx={{mt: 2}}>
                  <Box sx={{width: '100%', display: 'grid'}}>
                    <Grid container spacing={1}>
                      <Grid item>
                        <Box
                          sx={{
                            borderRadius: 2,
                            position: 'relative',
                            height: '50px',
                            width: '50px',
                            overflow: 'hidden',
                            border: '0.5px solid',
                            borderColor: '#7E7E7E33',
                          }}
                        >
                          <Image
                            src={adObj.imageUrl || adObj['Image Url']}
                            alt={adObj.title}
                            fill
                            quality={50}
                            placeholder="blur"
                            blurDataURL={adObj.imageUrl || adObj['Image Url']}
                            style={{
                              borderRadius: '8px',
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                      </Grid>

                      <Grid item xxs>
                        <Link
                          title={adObj.title}
                          href={
                            adObj?.link
                              ? `${adObj.link}?reforg=sd&refinv=lg`
                              : getOldLink(
                                  `/course/${kebabCase(adObj.title)}-${
                                    adObj.courseId
                                  }`
                                ) + '?reforg=sd&refinv=lg'
                          }
                          onClick={() => {
                            callEvent('click');
                            trackEvent(adObj);
                          }}
                          target="_blank"
                          color="gray.dark"
                          fontSize="13px"
                          boxSizing="border-box"
                          rel="nofollow"
                          sx={{
                            textDecoration: 'none',
                            cursor: 'pointer',
                            alt: adObj.title,
                            ':hover': {
                              color: 'secondary.main',
                            },
                          }}
                        >
                          {adObj.institute && (
                            <Typography
                              variant="subtitle3"
                              lineHeight="16px"
                              component={'p'}
                              color="secondary.main"
                            >
                              {adObj.institute}
                            </Typography>
                          )}
                          <Typography
                            variant="body3"
                            component={'p'}
                            mt={0.5}
                            lineHeight={'14px'}
                          >
                            {adObj?.title?.includes('|')
                              ? adObj?.title?.split('|')[1]?.trim()
                              : adObj?.title}
                          </Typography>
                        </Link>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              ))}
            </Card>
          </>
        )
      ) : (
        <Card
          sx={{
            mt: titleOut ? 1.5 : 2,
            p: 2,
            borderRadius: titleOut ? 2 : 4,
            mb: titleOut ? 3.75 : 0,
          }}
          id={`job_ads_banner_${label?.toLowerCase()?.replace(/ /g, '-')}`}
          data-testid={`job_ads_banner_${label
            ?.toLowerCase()
            ?.replace(/ /g, '-')}`}
        >
          <Skeleton
            variant="rounded"
            width={'50%'}
            height={21}
            sx={{
              mt: {xxs: 0, md: 1},
              ml: {xxs: 2, md: 'none'},
              mb: 1,
            }}
          />

          {[...new Array(10)].map(index => (
            <Box display="flex" key={`ads-learnngrow-skel-${index}`}>
              <Skeleton
                variant="rounded"
                width={'20%'}
                height={50}
                sx={{
                  mt: {xxs: 0, md: 1},
                  ml: {xxs: 1, md: 'none'},
                  mb: 1,
                }}
              />
              <Box display="block" width="100%">
                <Skeleton
                  variant="rounded"
                  width={'50%'}
                  height={20}
                  sx={{
                    mt: {xxs: 0, md: 1},
                    ml: {xxs: 2, md: 'none'},
                    mb: 1,
                  }}
                />
                <Skeleton
                  variant="rounded"
                  width={'90%'}
                  height={20}
                  sx={{
                    mt: {xxs: 0, md: 1},
                    ml: {xxs: 2, md: 'none'},
                    mb: 1,
                  }}
                />
              </Box>
            </Box>
          ))}
        </Card>
      )}
    </>
  );
};

export default LearnGrow;
